/**
 * KBDocumentRow — single row in the KB document table.
 * Extracted to keep KBDocumentList under 200 lines.
 */
import { Trash2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { KBVersionAccordion } from './KBVersionAccordion';
import {
  DOC_TYPE_LABELS,
  DOC_TYPE_CLASSES,
  formatKbDate as formatDate,
} from './kbDocConstants';
import type { KBDocumentResponse } from '@/hooks/useKBDocuments';

function IndexingStatusCell({ doc }: { doc: KBDocumentResponse }) {
  const status = doc.indexing_status ?? (doc.indexed_at ? 'indexed' : 'indexing');
  if (status === 'indexed') {
    return (
      <span role="status" className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Indexed
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span role="status" title="Indexing failed. The document may be corrupted or empty."
        className="flex items-center gap-1.5 text-sm text-destructive cursor-help">
        <XCircle className="h-4 w-4" aria-hidden="true" /> Failed
      </span>
    );
  }
  return (
    <span role="status" className="flex items-center gap-1.5 text-sm text-blue-500">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Indexing…
    </span>
  );
}

interface KBDocumentRowProps {
  doc: KBDocumentResponse;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete: (doc: KBDocumentResponse) => void;
}

export function KBDocumentRow({ doc, expanded, onToggleExpand, onDelete }: KBDocumentRowProps) {
  return (
    <>
      <tr className={cn('border-b border-border last:border-0 hover:bg-muted/20 transition-colors', expanded && 'bg-muted/20')}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{doc.title}</span>
            {doc.client_id === null && <Badge variant="outline" className="text-xs">Global</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">{doc.source}</p>
        </td>
        <td className="px-4 py-3">
          <Badge className={cn('text-xs', DOC_TYPE_CLASSES[doc.doc_type])}>
            {DOC_TYPE_LABELS[doc.doc_type]}
          </Badge>
        </td>
        <td className="px-4 py-3 text-muted-foreground">{doc.jurisdiction ?? '—'}</td>
        <td className="px-4 py-3">
          <button type="button" onClick={onToggleExpand}
            aria-expanded={expanded} aria-label={`View version history for ${doc.title}`}
            className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium hover:bg-secondary/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
            v{doc.version}
          </button>
        </td>
        <td className="px-4 py-3 text-muted-foreground text-xs">{formatDate(doc.indexed_at)}</td>
        <td className="px-4 py-3"><IndexingStatusCell doc={doc} /></td>
        <td className="px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => { onDelete(doc); }}
            aria-label={`Delete ${doc.title}`} className="text-muted-foreground hover:text-destructive">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="px-4 py-3 bg-muted/10">
            <KBVersionAccordion docId={doc.id} currentVersion={doc.version} />
          </td>
        </tr>
      )}
    </>
  );
}
